import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { authFetch } from "@/lib/authFetch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ExternalLink, AlertTriangle, Package, Building2, User } from "lucide-react";
import type { Commande } from "@shared/schema";
import { useProductImageResolver } from "@/hooks/use-inventory";

function formatDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatMontant(m: string | null | undefined) {
  if (!m) return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(parseFloat(m));
}

const STATUTS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: "Brouillon", color: "bg-gray-100 text-gray-700" },
  confirmed: { label: "Confirmée", color: "bg-blue-100 text-blue-700" },
  invoiced: { label: "Facturée", color: "bg-indigo-100 text-indigo-700" },
  shipped: { label: "Expédiée", color: "bg-orange-100 text-orange-700" },
  partially_shipped: { label: "Partiellement expédiée", color: "bg-yellow-100 text-yellow-700" },
  delivered: { label: "Livrée", color: "bg-green-100 text-green-700" },
  fulfilled: { label: "Livrée", color: "bg-green-100 text-green-700" },
  cancelled: { label: "Annulée", color: "bg-red-100 text-red-700" },
  void: { label: "Annulée", color: "bg-red-100 text-red-700" },
};

function statutBadge(statut: string | null | undefined) {
  const s = statut?.toLowerCase() || "";
  const info = STATUTS_LABELS[s] || { label: statut || "Inconnu", color: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${info.color}`}>
      {info.label}
    </span>
  );
}

type LigneItem = {
  sku?: string;
  nom: string;
  quantite: number;
  prixUnitaire: number;
  prixTotal: number;
  imageUrl?: string | null;
};

export default function CommandeDetail() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const resolveImage = useProductImageResolver();

  const { data: commande, isLoading } = useQuery<Commande>({
    queryKey: ["/api/commandes", params.id],
    queryFn: () => authFetch(`/api/commandes/${params.id}`).then(r => r.json()),
  });

  const lignes: LigneItem[] = (commande?.lignes as any) || [];
  const totalLignes = lignes.reduce((sum, l) => sum + (l.prixTotal || 0), 0);

  const zohoUrl = (commande?.zohoRaw as any)?.salesorder_url
    || (commande?.zohoId ? `https://inventory.zoho.eu/app#/salesorders/${commande.zohoId}` : null);

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!commande) {
    return (
      <div className="p-6 text-center">
        <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
        <p className="font-medium">Commande introuvable</p>
        <Button variant="ghost" onClick={() => navigate("/commandes")} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/commandes")} data-testid="button-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-display font-bold" data-testid="text-commande-numero">
              {commande.zohoNumero || commande.id.slice(0, 8)}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              {statutBadge(commande.statut)}
              {commande.type === "revendeur"
                ? <Badge className="bg-violet-100 text-violet-700 border-0">Revendeur</Badge>
                : <Badge className="bg-blue-100 text-blue-700 border-0">Client</Badge>
              }
            </div>
          </div>
        </div>
        {zohoUrl && (
          <Button variant="outline" onClick={() => window.open(zohoUrl, "_blank")} data-testid="button-open-zoho">
            <ExternalLink className="w-4 h-4 mr-2" />
            Ouvrir dans Zoho
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left column — general info */}
        <Card className="border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Informations générales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              {commande.type === "revendeur"
                ? <Building2 className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
                : <User className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
              }
              <div>
                <p className="text-xs text-muted-foreground">Destinataire</p>
                <p className="font-medium text-sm">{commande.contactNom || "—"}</p>
                {commande.clientId && (
                  <button
                    className="text-xs text-primary hover:underline mt-0.5"
                    onClick={() => navigate(`/clients/${commande.clientId}`)}
                    data-testid="link-client"
                  >
                    Voir la fiche client →
                  </button>
                )}
                {commande.resellerId && (
                  <button
                    className="text-xs text-primary hover:underline mt-0.5"
                    onClick={() => navigate(`/resellers/${commande.resellerId}`)}
                    data-testid="link-reseller"
                  >
                    Voir la fiche revendeur →
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2 border-t">
              <div>
                <p className="text-xs text-muted-foreground">Date commande</p>
                <p className="text-sm font-medium">{formatDate(commande.dateCommande)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Livraison prévue</p>
                <p className="text-sm font-medium">{formatDate(commande.dateLivraisonPrevue)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-2 border-t">
              <div>
                <p className="text-xs text-muted-foreground">Montant HT</p>
                <p className="text-lg font-bold">{formatMontant(commande.montantHt)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Montant TTC</p>
                <p className="text-lg font-bold text-muted-foreground">{formatMontant(commande.montantTtc)}</p>
              </div>
            </div>

            {commande.devise && commande.devise !== "EUR" && (
              <p className="text-xs text-muted-foreground">Devise : {commande.devise}</p>
            )}

            {commande.derniereSync && (
              <p className="text-xs text-muted-foreground pt-2 border-t">
                Dernière synchronisation : {new Date(commande.derniereSync).toLocaleString("fr-FR")}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Right column — lignes produits */}
        <Card className="border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Produits commandés</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {lignes.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Aucune ligne produit</p>
              </div>
            ) : (
              <div>
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Produit</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Qté</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">P.U.</th>
                      <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.map((l, i) => {
                      const imgSrc = resolveImage(l.sku, l.imageUrl);
                      return (
                      <tr key={i} className="border-t" data-testid={`row-ligne-${i}`}>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            {imgSrc ? (
                              <img src={imgSrc} alt={l.nom} className="w-9 h-9 rounded object-cover border border-border shrink-0" />
                            ) : (
                              <div className="w-9 h-9 rounded bg-muted flex items-center justify-center shrink-0">
                                <Package className="w-4 h-4 text-muted-foreground" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium leading-tight">{l.nom}</p>
                              {l.sku ? (
                                <p className="text-xs text-muted-foreground">{l.sku}</p>
                              ) : (
                                <p className="text-xs text-amber-600 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" /> SKU inconnu
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5 text-right">{l.quantite}</td>
                        <td className="px-4 py-2.5 text-right text-muted-foreground">
                          {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(l.prixUnitaire)}
                        </td>
                        <td className="px-4 py-2.5 text-right font-medium">
                          {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(l.prixTotal)}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                  <tfoot className="border-t bg-muted/30">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right font-semibold">Total HT</td>
                      <td className="px-4 py-3 text-right font-bold text-base">
                        {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(totalLignes)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
